/**
 * The map: Google Maps as the basemap, deck.gl for the demand grid.
 *
 * deck.gl rather than `google.maps.Data` because the grid is restyled on every tick of
 * the time slider. A few hundred Data-layer polygons re-styled at that rate stutters;
 * a WebGL layer does not. It is also what Google itself now points to, having retired
 * its own heatmap layer.
 *
 * ── Known limitation, by design ───────────────────────────────────────────────
 *
 * The overlay draws above the *entire* basemap, street labels included. The previous
 * MapLibre basemap could insert the grid beneath the label layers; a raster Google map
 * offers no equivalent. So label legibility rests entirely on OVERLAY_ALPHA in theme.ts
 * being low enough, and that is the first thing to adjust if names look washed out.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import type { Layer } from '@deck.gl/core';
import { PolygonLayer, ScatterplotLayer } from '@deck.gl/layers';
import { cellToBoundary } from 'h3-js';
import type { ScoredCell } from '../scoring/score.js';
import {
  COLORS,
  OVERLAY_LINE_WIDTH_M,
  OVERLAY_LINE_WIDTH_SELECTED_M,
  hexToRgb,
  rampColorRgba,
  rampLineRgba,
} from '../theme.js';
import { DARK_MAP_STYLE } from './mapStyle.js';
import { LOADER_OPTIONS, MAP_OPTIONS, bboxToBounds } from './mapConfig.js';
import { useGeolocation } from './useGeolocation.js';

declare global {
  interface Window {
    /**
     * Google calls this global when a key is missing, unauthorised, or the API is not
     * enabled. It is the only signal for that case — the loader promise still resolves —
     * so without hooking it, a bad key just looks like a blank map.
     */
    gm_authFailure?: () => void;
  }
}

export type MapFailure = 'auth' | 'network';

interface Props {
  apiKey: string;
  cells: readonly ScoredCell[];
  selected: string | null;
  onSelect: (h3: string | null) => void;
  bbox: [number, number, number, number];
  /** Reported when Google cannot be used at all, so the app can fall back to the list. */
  onFailure: (reason: MapFailure) => void;
}

/**
 * Hex boundaries never change for a given cell, so they are computed once and kept.
 * Recomputing a few hundred boundaries on every slider tick would be pure waste.
 */
const boundaryCache = new Map<string, number[][]>();

function boundaryOf(h3: string): number[][] {
  let boundary = boundaryCache.get(h3);
  if (!boundary) {
    // `true` asks h3-js for GeoJSON ordering — [lng, lat] — which is what deck.gl wants.
    boundary = cellToBoundary(h3, true);
    boundaryCache.set(h3, boundary);
  }
  return boundary;
}

export function GoogleHotspotMap({
  apiKey,
  cells,
  selected,
  onSelect,
  bbox,
  onFailure,
}: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const overlayRef = useRef<GoogleMapsOverlay | null>(null);
  const [ready, setReady] = useState(false);

  const geo = useGeolocation();

  // Keep the latest selection reachable from deck.gl accessors without rebuilding the
  // map, and let the click handler stay stable.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // Create the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let cancelled = false;

    // Google reports an invalid or unauthorised key through this global rather than by
    // rejecting the loader promise, so it has to be hooked explicitly — otherwise a bad
    // key looks like a silently blank map.
    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      onFailure('auth');
      previousAuthFailure?.();
    };

    const loader = new Loader({ apiKey, ...LOADER_OPTIONS });

    loader
      .importLibrary('maps')
      .then(({ Map }) => {
        if (cancelled || !containerRef.current) return;

        const map = new Map(containerRef.current, {
          ...MAP_OPTIONS,
          styles: DARK_MAP_STYLE,
          center: { lat: (bbox[0] + bbox[2]) / 2, lng: (bbox[1] + bbox[3]) / 2 },
          zoom: 13,
        });

        // Open framed on the whole working area: the first glance should answer which
        // part of the city is alive, not drop you into one block of it.
        map.fitBounds(bboxToBounds(bbox), 16);

        const overlay = new GoogleMapsOverlay({
          layers: [],
          onClick: (info) => {
            const cell = info.object as ScoredCell | undefined;
            onSelectRef.current(cell?.h3 ?? null);
          },
        });
        overlay.setMap(map);

        // A tap on bare map, outside any hex, clears the selection.
        map.addListener('click', () => onSelectRef.current(null));

        mapRef.current = map;
        overlayRef.current = overlay;
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) onFailure('network');
      });

    return () => {
      cancelled = true;
      window.gm_authFailure = previousAuthFailure;
      overlayRef.current?.finalize();
      overlayRef.current = null;
      mapRef.current = null;
    };
    // Mount-only: rebuilding the map on prop changes would fight the user's pan and zoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Repaint whenever the scores or the selection change.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay || !ready) return;

    const demand = new PolygonLayer<ScoredCell>({
      id: 'demand-cells',
      data: cells as ScoredCell[],
      getPolygon: (cell) => boundaryOf(cell.h3),
      getFillColor: (cell) => rampColorRgba(cell.normalized, cell.h3 === selected),
      getLineColor: (cell) => rampLineRgba(cell.normalized, cell.h3 === selected),
      getLineWidth: (cell) =>
        cell.h3 === selected ? OVERLAY_LINE_WIDTH_SELECTED_M : OVERLAY_LINE_WIDTH_M,
      lineWidthUnits: 'meters',
      lineWidthMinPixels: 1,
      stroked: true,
      filled: true,
      pickable: true,
      // Accessors are functions of both the scores and the selection, so deck.gl has to
      // be told when either changes or it will keep the previous frame's colours.
      updateTriggers: {
        getFillColor: [cells, selected],
        getLineColor: [cells, selected],
        getLineWidth: [selected],
      },
    });

    const layers: Layer[] = [demand];

    if (geo.position) {
      const [r, g, b] = hexToRgb(COLORS.textPrimary);
      layers.push(
        new ScatterplotLayer<{ lat: number; lng: number }>({
          id: 'rider-position',
          data: [geo.position],
          getPosition: (p) => [p.lng, p.lat],
          getFillColor: [r, g, b, 255],
          getLineColor: hexToRgb(COLORS.surface).concat(255) as [number, number, number, number],
          lineWidthMinPixels: 3,
          stroked: true,
          getRadius: 7,
          radiusUnits: 'pixels',
          pickable: false,
        }),
      );
    }

    overlay.setProps({ layers });
  }, [cells, selected, ready, geo.position]);

  // Recentre on the rider the first time a fix arrives, then leave the map alone —
  // yanking the viewport mid-pan while someone is reading it is worse than not helping.
  const hasCentred = useRef(false);
  useEffect(() => {
    if (!geo.position || hasCentred.current || !mapRef.current) return;
    hasCentred.current = true;
    mapRef.current.panTo({ lat: geo.position.lat, lng: geo.position.lng });
    mapRef.current.setZoom(15);
  }, [geo.position]);

  const handleLocate = useCallback(() => {
    if (geo.status === 'tracking' && geo.position && mapRef.current) {
      mapRef.current.panTo({ lat: geo.position.lat, lng: geo.position.lng });
      mapRef.current.setZoom(15);
      return;
    }
    geo.start();
  }, [geo]);

  return (
    <div className="map-wrap">
      <div ref={containerRef} className="map-canvas" />
      <button
        type="button"
        className={geo.status === 'tracking' ? 'locate-button locate-button-active' : 'locate-button'}
        onClick={handleLocate}
        aria-label={geo.status === 'tracking' ? 'Centre on my location' : 'Find my location'}
        aria-pressed={geo.status === 'tracking'}
      >
        {geo.status === 'locating' ? '…' : '◎'}
      </button>
      {geo.status === 'denied' && (
        <p className="locate-note" role="status">
          Location permission denied — the map still works, it just won&rsquo;t follow you.
        </p>
      )}
    </div>
  );
}
